import type { LucideIcon } from "lucide-react";

// A live product preview for the configurator's summary rail. Renders a simple
// mockup that reflects the chosen spec — aspect ratio from size, rounded corners
// / lamination sheen from finishing, mono vs colour from print. Stocked and
// bought-in items show a product tile instead of a printed sheet. Purely
// presentational; the page derives the props from the current selections.

export type PreviewVariant = "sheet" | "booklet" | "stack" | "product";

export interface PreviewProps {
  name: string;
  variant: PreviewVariant;
  /** width / height of the printed piece. */
  aspect: number;
  rounded: boolean;
  laminated: boolean;
  mono: boolean;
  icon: LucideIcon;
}

function fitBox(
  aspect: number,
  maxW = 92,
  maxH = 68,
): { w: number; h: number } {
  let w: number;
  let h: number;
  if (aspect >= 1) {
    w = maxW;
    h = w / aspect;
    if (h > maxH) {
      h = maxH;
      w = h * aspect;
    }
  } else {
    h = maxH;
    w = h * aspect;
    if (w > maxW) {
      w = maxW;
      h = w / aspect;
    }
  }
  return { w: Math.round(w), h: Math.round(h) };
}

function Sheet({
  aspect,
  rounded,
  laminated,
  mono,
  offset = 0,
  shadow = true,
}: {
  aspect: number;
  rounded: boolean;
  laminated: boolean;
  mono: boolean;
  offset?: number;
  shadow?: boolean;
}) {
  const { w, h } = fitBox(aspect);
  const accent = mono ? "#9ca3af" : "var(--color-primary)";
  return (
    <div
      className="relative overflow-hidden bg-white"
      style={{
        width: w,
        height: h,
        borderRadius: rounded ? 12 : 3,
        boxShadow: shadow ? "0 10px 30px -8px rgba(0,0,0,0.35)" : "none",
        transform: offset ? `translate(${offset}px, ${-offset}px)` : undefined,
        border: "1px solid rgba(0,0,0,0.06)",
      }}
    >
      {/* faux content */}
      <div className="absolute inset-0 p-3 flex flex-col justify-end gap-1.5">
        <div
          className="h-1.5 rounded-full"
          style={{ width: "55%", background: accent }}
        />
        <div
          className="h-1 rounded-full bg-gray-200"
          style={{ width: "70%" }}
        />
        <div
          className="h-1 rounded-full bg-gray-200"
          style={{ width: "45%" }}
        />
      </div>
      <div
        className="absolute rounded-full"
        style={{
          top: 10,
          left: 12,
          width: 16,
          height: 16,
          background: accent,
          opacity: 0.85,
        }}
      />
      {/* lamination sheen */}
      {laminated && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(115deg, rgba(255,255,255,0) 30%, rgba(255,255,255,0.55) 45%, rgba(255,255,255,0) 60%)",
          }}
        />
      )}
    </div>
  );
}

export function CataloguePreview(props: PreviewProps) {
  const { variant, aspect, rounded, laminated, mono, icon: Icon } = props;

  return (
    <div className="flex items-center justify-center bg-muted p-3">
      {variant === "product" ? (
        <div className="flex size-14 items-center justify-center rounded-xl bg-card shadow-sm text-muted-foreground">
          <Icon className="size-7" />
        </div>
      ) : variant === "stack" ? (
        <div className="relative" style={{ width: 150, height: 110 }}>
          {[0, 5, 10].map((o, i) => (
            <div
              key={o}
              className="absolute"
              style={{ left: o, top: 10 - i * 3 }}
            >
              <Sheet
                aspect={aspect}
                rounded={false}
                laminated={false}
                mono={i !== 0}
                shadow={i === 2}
              />
            </div>
          ))}
        </div>
      ) : variant === "booklet" ? (
        (() => {
          const d = fitBox(aspect);
          const pageEdge: React.CSSProperties = {
            position: "absolute",
            width: d.w,
            height: d.h,
            background: "white",
            borderRadius: 3,
            border: "1px solid rgba(0,0,0,0.08)",
          };
          return (
            <div
              className="relative"
              style={{ width: d.w + 14, height: d.h + 8 }}
            >
              {/* inner page edges peeking out behind the cover */}
              <div
                style={{
                  ...pageEdge,
                  left: 12,
                  top: 6,
                  boxShadow: "0 6px 18px -8px rgba(0,0,0,0.3)",
                }}
              />
              <div style={{ ...pageEdge, left: 6, top: 3 }} />
              {/* cover, with a spine down the left edge */}
              <div className="absolute left-0 top-0">
                <Sheet
                  aspect={aspect}
                  rounded={rounded}
                  laminated={laminated}
                  mono={mono}
                />
                <div
                  className="absolute inset-y-0 left-0 w-2 rounded-l-[3px]"
                  style={{
                    background: mono ? "#6b7280" : "var(--color-primary)",
                    opacity: 0.55,
                  }}
                />
              </div>
            </div>
          );
        })()
      ) : (
        <Sheet
          aspect={aspect}
          rounded={rounded}
          laminated={laminated}
          mono={mono}
        />
      )}
    </div>
  );
}
